import React, { useState, useEffect } from 'react';
import {
	Card,
	CardContent,
	Typography,
	Button,
	CircularProgress,
	Alert,
	Box,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Chip,
	IconButton,
	Tooltip,
	TextField,
	Divider,
	ToggleButtonGroup,
	ToggleButton,
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import PublicIcon from '@mui/icons-material/Public';
import GroupIcon from '@mui/icons-material/Group';
import PersonIcon from '@mui/icons-material/Person';
import { Title, useNotify } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import {
	Template,
	getTemplates,
	syncUseCases,
	syncUseCasesToQdrant,
	getQdrantUseCasesStats,
	searchUseCasesQdrant,
	indexUseCaseToQdrant,
	UseCaseQdrantStats,
	UseCaseSearchResult,
} from '../../Services/quantiAI';

export const AIUseCasesList = () => {
	const [templates, setTemplates] = useState<Template[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [syncing, setSyncing] = useState(false);
	const [syncingQdrant, setSyncingQdrant] = useState(false);
	const [qdrantStats, setQdrantStats] = useState<UseCaseQdrantStats | null>(null);
	const [searchQuery, setSearchQuery] = useState('');
	const [searching, setSearching] = useState(false);
	const [searchResults, setSearchResults] = useState<UseCaseSearchResult[] | null>(null);
	const [indexingId, setIndexingId] = useState<string | null>(null);
	const [scopeFilter, setScopeFilter] = useState<'all' | 'general' | 'specific'>('all');
	const notify = useNotify();
	const navigate = useNavigate();

	const loadTemplates = async () => {
		setLoading(true);
		setError(null);
		try {
			const response = await getTemplates();
			setTemplates(response.data || []);
		} catch (err: any) {
			setError(err.message || 'Impossible de contacter le serveur Quanti AI');
		} finally {
			setLoading(false);
		}
	};

	const loadQdrantStats = async () => {
		try {
			const stats = await getQdrantUseCasesStats();
			setQdrantStats(stats);
		} catch (err) {
			// Silently ignore - Qdrant stats are optional
		}
	};

	useEffect(() => {
		loadTemplates();
		loadQdrantStats();
	}, []);

	const handleSync = async () => {
		setSyncing(true);
		try {
			const result = await syncUseCases();
			notify(
				`Synchronisation terminée : ${result.created} créé(s), ${result.updated} mis à jour, ${result.errors} erreur(s)`,
				{ type: result.errors > 0 ? 'warning' : 'success' }
			);
			loadTemplates();
		} catch (err: any) {
			notify(err.message || 'Erreur lors de la synchronisation', {
				type: 'error',
			});
		} finally {
			setSyncing(false);
		}
	};

	const handleSyncQdrant = async () => {
		setSyncingQdrant(true);
		try {
			const result = await syncUseCasesToQdrant();
			notify(
				`Indexation Qdrant : ${result.indexed}/${result.total_use_cases} use cases indexés`,
				{ type: result.errors > 0 ? 'warning' : 'success' }
			);
			loadQdrantStats();
		} catch (err: any) {
			notify(err.message || "Erreur lors de l'indexation Qdrant", {
				type: 'error',
			});
		} finally {
			setSyncingQdrant(false);
		}
	};

	const handleSearch = async () => {
		if (!searchQuery.trim()) {
			setSearchResults(null);
			return;
		}
		setSearching(true);
		try {
			const response = await searchUseCasesQdrant(searchQuery, 10);
			setSearchResults(response.results);
		} catch (err: any) {
			notify(err.message || 'Erreur lors de la recherche', { type: 'error' });
		} finally {
			setSearching(false);
		}
	};

	const handleKeyPress = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			handleSearch();
		}
	};

	const clearSearch = () => {
		setSearchQuery('');
		setSearchResults(null);
	};

	const handleView = (slug: string) => {
		navigate(`/ai-use-cases/${slug}`);
	};

	const handleIndexOne = async (id: string, name: string) => {
		setIndexingId(id);
		try {
			await indexUseCaseToQdrant(id);
			notify(`"${name}" indexé dans Qdrant`, { type: 'success' });
			loadQdrantStats();
		} catch (err: any) {
			notify(err.message || "Erreur lors de l'indexation", { type: 'error' });
		} finally {
			setIndexingId(null);
		}
	};

	return (
		<Card>
			<Title title="Quanti AI - Use Cases" />
			<CardContent>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						mb: 3,
					}}
				>
					<Box>
						<Typography variant="h5">Templates de Use Cases</Typography>
						{qdrantStats && (
							<Typography variant="body2" color="text.secondary">
								{qdrantStats.total_points} use cases indexés dans Qdrant
							</Typography>
						)}
					</Box>
					<Box sx={{ display: 'flex', gap: 1 }}>
						<Tooltip title="Synchroniser depuis Git vers PostgreSQL">
							<Button
								variant="outlined"
								startIcon={
									syncing ? <CircularProgress size={20} /> : <SyncIcon />
								}
								onClick={handleSync}
								disabled={syncing || syncingQdrant}
							>
								Sync Git
							</Button>
						</Tooltip>
						<Tooltip title="Indexer dans Qdrant pour la recherche sémantique">
							<Button
								variant="outlined"
								color="secondary"
								startIcon={
									syncingQdrant ? (
										<CircularProgress size={20} />
									) : (
										<CloudSyncIcon />
									)
								}
								onClick={handleSyncQdrant}
								disabled={syncing || syncingQdrant}
							>
								Sync Qdrant
							</Button>
						</Tooltip>
					</Box>
				</Box>

				{/* Recherche sémantique */}
				<Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
					<Typography variant="subtitle2" gutterBottom>
						Recherche sémantique (Qdrant)
					</Typography>
					<Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
						<TextField
							size="small"
							fullWidth
							placeholder="Ex: Comment comparer les performances des campagnes publicitaires ?"
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							onKeyDown={handleKeyPress}
						/>
						<Button
							variant="contained"
							onClick={handleSearch}
							disabled={searching || !searchQuery.trim()}
							startIcon={
								searching ? <CircularProgress size={20} /> : <SearchIcon />
							}
						>
							Rechercher
						</Button>
						{searchResults && (
							<Button variant="text" onClick={clearSearch}>
								Effacer
							</Button>
						)}
					</Box>

					{/* Résultats de recherche */}
					{searchResults && (
						<Box sx={{ mt: 2 }}>
							<Divider sx={{ mb: 2 }} />
							<Typography variant="subtitle2" gutterBottom>
								{searchResults.length} résultat(s) pour "{searchQuery}"
							</Typography>
							{searchResults.length > 0 ? (
								<TableContainer>
									<Table size="small">
										<TableHead>
											<TableRow>
												<TableCell>Score</TableCell>
												<TableCell>Code</TableCell>
												<TableCell>Nom</TableCell>
												<TableCell>Catégorie</TableCell>
												<TableCell>Semantic Codes</TableCell>
												<TableCell align="right">Actions</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{searchResults.map((result) => (
												<TableRow key={result.id}>
													<TableCell>
														<Chip
															label={`${(result.score * 100).toFixed(1)}%`}
															size="small"
															color={
																result.score > 0.8
																	? 'success'
																	: result.score > 0.6
																		? 'warning'
																		: 'default'
															}
														/>
													</TableCell>
													<TableCell>
														<code style={{ fontSize: '0.7rem' }}>
															{result.code}
														</code>
													</TableCell>
													<TableCell>{result.name}</TableCell>
													<TableCell>
														{result.category && (
															<Chip label={result.category} size="small" />
														)}
													</TableCell>
													<TableCell>
														<Box
															sx={{
																display: 'flex',
																gap: 0.5,
																flexWrap: 'wrap',
															}}
														>
															{result.semantic_codes_required
																?.slice(0, 3)
																.map((code) => (
																	<Chip
																		key={code}
																		label={code}
																		size="small"
																		variant="outlined"
																		color="error"
																	/>
																))}
															{result.semantic_codes_required &&
																result.semantic_codes_required.length > 3 && (
																	<Chip
																		label={`+${result.semantic_codes_required.length - 3}`}
																		size="small"
																		variant="outlined"
																	/>
																)}
														</Box>
													</TableCell>
													<TableCell align="right">
														<IconButton
															size="small"
															onClick={() => handleView(result.code)}
															title="Voir le détail"
														>
															<VisibilityIcon />
														</IconButton>
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</TableContainer>
							) : (
								<Alert severity="info">Aucun résultat trouvé</Alert>
							)}
						</Box>
					)}
				</Paper>

				{error && (
					<Alert severity="error" sx={{ mb: 2 }}>
						{error}
					</Alert>
				)}

				{loading ? (
					<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
						<CircularProgress />
					</Box>
				) : templates.length > 0 ? (
					<>
					<Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
						<Typography variant="body2" color="text.secondary">Scope :</Typography>
						<ToggleButtonGroup
							size="small"
							value={scopeFilter}
							exclusive
							onChange={(_, val) => val && setScopeFilter(val)}
						>
							<ToggleButton value="all">Tous</ToggleButton>
							<ToggleButton value="general">Général</ToggleButton>
							<ToggleButton value="specific">Spécifique</ToggleButton>
						</ToggleButtonGroup>
					</Box>
					<TableContainer component={Paper}>
						<Table>
							<TableHead>
								<TableRow>
									<TableCell>Slug</TableCell>
									<TableCell>Nom</TableCell>
									<TableCell align="center">Scope</TableCell>
									<TableCell>SKUs requis</TableCell>
									<TableCell>Catégorie</TableCell>
									<TableCell>Tags</TableCell>
									<TableCell>Difficulté</TableCell>
									<TableCell>Statut</TableCell>
									<TableCell align="right">Actions</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{templates
									.filter((t) => {
										if (scopeFilter === 'general') return !t.required_skus?.length;
										if (scopeFilter === 'specific') return !!t.required_skus?.length;
										return true;
									})
									.map((template) => (
									<TableRow key={template.id}>
										<TableCell>
											<code style={{ fontSize: '0.75rem' }}>
												{template.slug}
											</code>
										</TableCell>
										<TableCell>{template.name}</TableCell>
										<TableCell align="center">
											<Tooltip title={
												template.scope === 'global' ? 'Global' :
												template.scope === 'project' ? `Projet${template.project_folder_id ? ` (${template.project_folder_id})` : ''}` :
												'Personnel'
											} arrow>
												{template.scope === 'global' ? (
													<PublicIcon fontSize="small" color="primary" />
												) : template.scope === 'project' ? (
													<GroupIcon fontSize="small" color="info" />
												) : (
													<PersonIcon fontSize="small" color="action" />
												)}
											</Tooltip>
										</TableCell>
										<TableCell>
											<Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
												{template.required_skus?.map((sku) => (
													<Chip key={sku} label={sku} size="small" color="info" />
												))}
											</Box>
										</TableCell>
										<TableCell>
											{template.category && (
												<Chip label={template.category} size="small" />
											)}
										</TableCell>
										<TableCell>
											<Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
												{template.tags?.slice(0, 2).map((tag) => (
													<Chip
														key={tag}
														label={tag}
														size="small"
														variant="outlined"
													/>
												))}
												{template.tags && template.tags.length > 2 && (
													<Chip
														label={`+${template.tags.length - 2}`}
														size="small"
														variant="outlined"
													/>
												)}
											</Box>
										</TableCell>
										<TableCell>{template.difficulty}</TableCell>
										<TableCell>
											{template.status === 'published' ? (
												<Chip label="Publié" color="success" size="small" />
											) : (
												<Chip label="Brouillon" size="small" />
											)}
											{template.is_featured && (
												<Chip
													label="Featured"
													color="primary"
													size="small"
													sx={{ ml: 0.5 }}
												/>
											)}
										</TableCell>
										<TableCell align="right">
											<Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
												<Tooltip title="Indexer dans Qdrant">
													<IconButton
														size="small"
														onClick={() => handleIndexOne(template.id, template.name)}
														disabled={indexingId === template.id}
													>
														{indexingId === template.id ? (
															<CircularProgress size={20} />
														) : (
															<CloudSyncIcon />
														)}
													</IconButton>
												</Tooltip>
												<Tooltip title="Voir le détail">
													<IconButton
														size="small"
														onClick={() => handleView(template.slug)}
													>
														<VisibilityIcon />
													</IconButton>
												</Tooltip>
												<Tooltip title="Éditer">
													<IconButton
														size="small"
														onClick={() => navigate(`/ai-use-cases/${template.slug}?edit=true`)}
													>
														<EditIcon />
													</IconButton>
												</Tooltip>
											</Box>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				</>
				) : (
					<Alert severity="info">
						Aucun template de use case. Cliquez sur "Synchroniser" pour
						importer les use cases depuis le repository.
					</Alert>
				)}
			</CardContent>
		</Card>
	);
};
